import React from 'react'

interface Props {
  readonly imageUrls?: ReadonlyArray<string>
  readonly renderChildren: Function
}

interface State {
  readonly show: boolean
  readonly displayId: number
}

type AcceptedKeys = 'ArrowRight' | 'ArrowLeft' | 'Escape' | 'Space'

const LIGHTBOX_ID = 'light-box-container'

class LightBox extends React.PureComponent<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      show: false,
      displayId: 0,
    }
  }

  componentWillUnmount() {
    this.removeEventListeners()
  }

  navigateLeft = () => {
    const { show, displayId } = this.state
    if (show && displayId > 0) {
      this.setState(prevState => ({ displayId: prevState.displayId - 1 }))
    }
  }

  navigateRight = () => {
    const { show, displayId } = this.state
    const { imageUrls } = this.props
    if (imageUrls && show && displayId < imageUrls.length - 1) {
      this.setState(prevState => ({ displayId: prevState.displayId + 1 }))
    }
  }

  addEventListeners = () => {
    document.addEventListener('keydown', this.handleKeyPress)
    document.addEventListener('click', this.handleMouseClick)
  }

  removeEventListeners = () => {
    document.removeEventListener('keydown', this.handleKeyPress)
    document.removeEventListener('click', this.handleMouseClick)
  }

  handleKeyPress = event => {
    const key: AcceptedKeys = event.code
    switch (key) {
      case 'ArrowLeft':
        this.navigateLeft()
        break
      case 'Space':
      case 'ArrowRight':
        this.navigateRight()
        break
      case 'Escape':
        this.dismiss()
        break
    }
  }

  handleMouseClick = event => {
    const targetId = event.target.id
    if (targetId === LIGHTBOX_ID) {
      this.dismiss()
    }
  }

  onClick = imageIndex => {
    const { show } = this.state
    if (!show) {
      this.show(imageIndex)
    } else {
      this.setState({ displayId: imageIndex })
    }
  }

  setBodyStyling = (style: Partial<CSSStyleDeclaration>) => {
    Object.keys(style).map(key => (document.body.style[key] = style[key]))
  }

  show = defaultId => {
    this.setState({ displayId: defaultId, show: true }, () => {
      this.addEventListeners()
      document.body.classList.add('lightbox-showing')
    })
  }

  dismiss = () => {
    this.setState({ show: false }, () => {
      this.removeEventListeners()
      document.body.classList.remove('lightbox-showing')
    })
  }

  render() {
    const { show, displayId } = this.state
    const { imageUrls, renderChildren } = this.props

    return (
      <>
        {renderChildren(this.onClick)}
        {show && displayId >= 0 && imageUrls && (
          <div id={LIGHTBOX_ID}>
            <button onClick={this.navigateLeft}>
              <i
                className={`fal chevron-icon fa-chevron-left ${displayId > 0 ? '' : 'disabled'}`}
              />
            </button>
            <div className='lighbox-img-container'>
              <button className='lightbox-close' aria-label='Close' onClick={this.dismiss}>
                &times;
              </button>
              <img className='lightbox-img' src={imageUrls && imageUrls[displayId]} />
              <div className='lightbox-bullets'>
                {imageUrls.map((_, i) => (
                  <button onClick={() => this.onClick(i)} key={`lightbox-bullet-${i}`}>
                    <span
                      className={`lightbox-bullet bullet-${i} ${i === displayId && 'active'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <button onClick={this.navigateRight}>
              <i
                className={`fal chevron-icon fa-chevron-right ${
                  displayId < imageUrls.length - 1 ? '' : 'disabled'
                }`}
              />
            </button>
          </div>
        )}
      </>
    )
  }
}

export default LightBox
